// client/src/screens/admin/products/haberdashery/useHaberdasheryForm.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  SHOP_KEY,
  DRAFT_WRITE_DELAY_MS,
  buildInitialState,
  buildPayload,
  buildVariantRow,
  clearDraft,
  getDraftKey,
  normalizeImageArray,
  readDraft,
  validateForm,
  writeDraft,
} from "./HaberdasheryForm.shared";
import { getSafeUserMessage } from "../../../../utils/errorMessages";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function getConflictMessage(errorMeta, fallback = "") {
  if (Number(errorMeta?.status) !== 409) return "";

  switch (String(errorMeta?.code || "")) {
    case "VERSION_CONFLICT":
      return "This product was changed elsewhere. Reload before saving.";
    case "PRODUCT_SKU_EXISTS":
      return "A product or variant with this SKU already exists.";
    case "PRODUCT_SLUG_EXISTS":
      return "A product with this slug already exists in this shop.";
    case "PRODUCT_EXTERNAL_ID_EXISTS":
      return "A product with this external ID already exists in this shop.";
    default:
      return fallback || "Conflict detected.";
  }
}

/* -------------------------------------------------------------------------- */
/*                                   Hook                                     */
/* -------------------------------------------------------------------------- */

export default function useHaberdasheryForm({
  initialData,
  onSubmit,
  loading = false,
  error = "",
  errorMeta = null,
  saveSuccess = false,
  shopKey = SHOP_KEY,
}) {
  const draftKey = useMemo(() => getDraftKey(initialData), [initialData]);
  const hydratedRef = useRef(false);
  const writeTimerRef = useRef(null);

  const [form, setForm] = useState(() =>
    buildInitialState(initialData, readDraft(draftKey)),
  );
  const [submitError, setSubmitError] = useState("");
  const [draftRestored, setDraftRestored] = useState(
    Boolean(readDraft(draftKey)),
  );

  const submitDisabled = Boolean(loading);

  const conflictMessage = useMemo(
    () => getConflictMessage(errorMeta, error),
    [error, errorMeta],
  );

  useEffect(() => {
    const savedDraft = readDraft(draftKey);

    setForm(buildInitialState(initialData, savedDraft));
    setSubmitError("");
    setDraftRestored(Boolean(savedDraft));
    hydratedRef.current = true;
  }, [draftKey, initialData]);

  useEffect(() => {
    if (!hydratedRef.current) return;

    if (writeTimerRef.current) {
      window.clearTimeout(writeTimerRef.current);
    }

    writeTimerRef.current = window.setTimeout(() => {
      writeDraft(draftKey, form);
    }, DRAFT_WRITE_DELAY_MS);

    return () => {
      if (writeTimerRef.current) {
        window.clearTimeout(writeTimerRef.current);
      }
    };
  }, [draftKey, form]);

  useEffect(() => {
    if (!saveSuccess) return;
    clearDraft(draftKey);
  }, [draftKey, saveSuccess]);

  useEffect(() => {
    const handler = (event) => {
      if (loading) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);

    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [loading]);

  const setField = useCallback((key, value) => {
    setSubmitError("");

    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const setVariantField = useCallback((index, key, value) => {
    setSubmitError("");

    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, currentIndex) =>
        currentIndex === index
          ? {
              ...variant,
              [key]:
                key === "images"
                  ? normalizeImageArray(
                      typeof value === "function"
                        ? value(variant.images || [])
                        : value,
                    )
                  : value,
            }
          : variant,
      ),
    }));
  }, []);

  const setImages = useCallback((images) => {
    setSubmitError("");

    setForm((prev) => ({
      ...prev,
      images: normalizeImageArray(
        typeof images === "function" ? images(prev.images) : images,
      ),
    }));
  }, []);

  const addVariant = useCallback(() => {
    setSubmitError("");

    setForm((prev) => ({
      ...prev,
      variants: [...prev.variants, buildVariantRow()],
    }));
  }, []);

  const removeVariant = useCallback((index) => {
    setSubmitError("");

    setForm((prev) => ({
      ...prev,
      variants:
        prev.variants.length > 1
          ? prev.variants.filter((_, currentIndex) => currentIndex !== index)
          : [buildVariantRow()],
    }));
  }, []);

  const discardDraft = useCallback(() => {
    clearDraft(draftKey);
    setForm(buildInitialState(initialData, null));
    setSubmitError("");
    setDraftRestored(false);
  }, [draftKey, initialData]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (loading) return;

      const validationError = validateForm(form);

      if (validationError) {
        setSubmitError(validationError);
        return;
      }

      const payload = buildPayload({
        ...form,
        shopKey: shopKey || SHOP_KEY,
      });

      try {
        await onSubmit(payload);
      } catch (submitFailure) {
        setSubmitError(
          getSafeUserMessage(
            submitFailure?.message || error,
            "We could not save this haberdashery product right now. Please try again later.",
          ),
        );
      }
    },
    [error, form, loading, onSubmit, shopKey],
  );

  return {
    form,
    submitError,
    draftRestored,
    submitDisabled,
    conflictMessage,

    setField,
    setVariantField,
    setImages,
    addVariant,
    removeVariant,
    discardDraft,
    handleSubmit,
  };
}
